import get from 'lodash/get';
import getLocale from 'utils/locale';
import isNil from 'lodash/isNil';
import moment from 'moment-timezone';
import some from 'lodash/some';
import isEmpty from 'lodash/isEmpty';
import has from 'lodash/has';
import truncate from 'lodash/truncate';
import { calcDuration, getMinTime, toFormattedDDhhmm } from 'utils/time';
import { goToPlace } from 'utils/navigation';
import { IncidentTriggerSortOrder } from 'xm-constants/incidentTriggers';
import { MAX_DISPLAYED_INSIGHT } from 'xm-constants/insights';
import { RECIPIENT_TYPES } from 'xm-constants/recipients';
import { DELETED_USER_TYPES } from 'xm-constants/incidentManagement';

/**
 * getNameFromUserObject:
 * The server returns the owner as an object, but we just want a string.
 *
 * Also, an incident may not have an owner; an owner may not have first name, last name, and target name.
 * Use `get` to make sure it doesn't crash, and missing names won't cause "undefined" to be part of the owner string.
 * Result is like this: "Steve Taylor (staylor)".
 *
 * ^^ Same for reporter.
 *
 * @params {Object} incident - the incident returned from the server
 * @params {String} userKey - usually 'owner' or 'reporter'
 * @return {String} 'FirstName LastName (targetName)'
 */
export function getNameFromUserObject(incident, userKey) {
  if (
    !has(incident, [userKey, 'firstName']) ||
    !has(incident, [userKey, 'lastName'])
  ) {
    return null;
  }
  return `${get(incident, [userKey, 'firstName'], '')} ${get(
    incident,
    [userKey, 'lastName'],
    '',
  )} (${get(incident, [userKey, 'targetName'], '')})`;
}

export const isCompanySupervisor = loggedInUser =>
  loggedInUser.loggedInUserProfile.roles
    ? some(loggedInUser.loggedInUserProfile.roles.data, {
        name: 'Company Supervisor',
      })
    : false;

export const isCompanyAdmin = loggedInUser =>
  loggedInUser.loggedInUserProfile.roles
    ? some(loggedInUser.loggedInUserProfile.roles.data, {
        name: 'Company Admin',
      })
    : false;

const isCompanyAdminOrSupervisor = loggedInUser =>
  isCompanyAdmin(loggedInUser) || isCompanySupervisor(loggedInUser);

export const isOwner = (incident, loggedInUser) =>
  incident?.owner?.id === loggedInUser.loggedInUserProfile.id;

export const canReassignIncidentOwner = (incident, loggedInUser) => {
  const isIncidentOwner = isOwner(incident, loggedInUser);
  const adminOrSuper = isCompanyAdminOrSupervisor(loggedInUser);
  return isIncidentOwner || adminOrSuper;
};

export function goToIncidents() {
  goToPlace('incidents');
}

/**
 * Get encoded incident ID. If it cannot be encode/decoded, we use incident UUID in user friendly URL
 *
 * @param incidentUuid - incident uuid
 * @param incidentId - incident ID
 * @returns {string|*} either URL encoded incidentId or replaced by id (uuid).
 */
export function getEncodedIncidentId(incidentUuid, incidentId) {
  const incidentUriComponentEncoded = encodeURIComponent(incidentId);
  const incidentUriDecoded = decodeURI(incidentUriComponentEncoded);
  return incidentUriDecoded === incidentId
    ? incidentUriComponentEncoded
    : incidentUuid;
}

/**
 * Given an incident, return its impact start time.
 * @param {object} incident
 * @returns {string} The impact start time.
 */
export function getImpactStart(incident) {
  // Usually these properties are already formatted
  return incident?.impactStartAt ?? incident.createdAt;
}

/**
 * Use the incident's information to determine the impact end time.
 * If the user has set it manually before, we'll just use it. Otherwise, determine it from
 * the information we have.
 *
 * @returns {moment.Moment} - Returns a moment object instead of a timestamp string.
 */
export function getImpactEnd(incident) {
  const { impactEndAt, resolvedAt, mitigatedAt } = incident;

  return moment(
    impactEndAt ||
      getMinTime(resolvedAt, mitigatedAt) ||
      moment().toISOString(),
  );
}

const defaultConfig = {
  delimiter: ' ',
  spacer: '',
  units: ['d', 'h', 'm'],
  language: 'shortEn',
  languages: {
    shortEn: {
      d: () => 'd',
      h: () => 'h',
      m: () => 'm',
    },
  },
};

/**
 * Calculate the duration between an incident impact start time and impact end time;
 * result is in a human readable string like "23h 49m".
 *
 * @param {string|moment.Moment} startTime
 * @param {string|moment.Moment} endTime
 * @param {object} config - A defaultConfig is provided, but can be overridden.
 * @returns {string|null} - A human readable string
 */
export function getImpactDuration(startTime, endTime, config = defaultConfig) {
  return calcDuration({
    created: startTime,
    terminated: endTime,
    locale: getLocale(),
    config,
    displayZero: true,
  });
}

export function getImpactDurationWithMetrics(incident) {
  if (incident.metrics) {
    return toFormattedDDhhmm(incident.metrics.impactDuration, {
      roundUpMinutes: true,
    });
  }

  return getImpactDuration(getImpactStart(incident), getImpactEnd(incident));
}

/**
 * Sort triggers according to predefined order:
 * CRITICAL => ... => MINIMAL => OPEN => ... => REJECTED
 *
 * Intended to be used with Array.prototype.sort
 *
 * @param {String} triggerType1
 * @param {String} triggerType2
 * @returns {number} - A negative number if triggerType1 comes before triggerType2.
 *                   - A positive number if triggerType 1 comes after triggerType2.
 *                   - 0 if they're the same trigger
 */
export function sortTriggers(triggerType1, triggerType2) {
  return (
    IncidentTriggerSortOrder.indexOf(triggerType1) -
    IncidentTriggerSortOrder.indexOf(triggerType2)
  );
}

/**
 * Truncate the description down to 2,000 characters, and end it with '...'.
 * @param {string} [description=''] - original description
 * @returns {string} - truncated
 */
export function truncateDescription(description = '') {
  return truncate(description, {
    length: 2000,
  });
}

/**
 * Because insights has a priority, if there are many insights, there is no point
 * fetching all of them because the later ones are not important enough. In this case,
 * instead of displaying the total insights accurately, say, "2,356", just say "99+".
 *
 * @param {number} count - number of insights.
 * @param {number} [max=99] - the max. Anything more than this, just display "[max]+", like "99+".
 * @returns {string|number} - if less than or equal to max, return a number.
 *                            if more, return a string.
 *                            Will return 0 if count is 0. If the developer would like to display
 *                            nothing instead, they'll need to handle it where this function is called.
 */
export function getNormalizedInsightCount(count, max = MAX_DISPLAYED_INSIGHT) {
  return count > max ? `${max}+` : count;
}

export function isIncidentClosed(incident) {
  return ['Rejected', 'Resolved'].includes(incident.status.name);
}

export function getDisplayName(incident) {
  if (isEmpty(incident)) return '';

  return `${incident.incidentIdentifier ?? ''}: ${incident.summary ?? ''}`;
}

/**
 * Given an incident commander, a resolver, or a stakeholder, tell if the user/group inside is deleted or not.
 *
 * This function is designed specifically for getFilledRecipient below, and you almost never need to import this.
 * You very likely need the `isDeleted` function in `src/recipientSearch/index.js` instead.
 *
 * @param {object} originalRecipient - the user/group inside the commander/resolver/stakeholder
 * @param {string} type - commander? resolver? stakeholder?
 * @returns {boolean} - true if the recipient is deleted. false if not.
 */
export function isRecipientDeleted(originalRecipient, type) {
  if (isNil(originalRecipient)) return true;

  switch (type) {
    case DELETED_USER_TYPES.INCIDENT_COMMANDER:
    case DELETED_USER_TYPES.RESOLVER:
      return !originalRecipient.resolver;

    case DELETED_USER_TYPES.STAKEHOLDER:
      return isNil(originalRecipient);

    default:
      return false;
  }
}

/**
 * The Bespin team has changed the incident console server code, so if a user is soft & hard deleted, only null is returned.
 * However, most components don't expect that, and many will crash. To solve this issue, and to display the correct text in some areas,
 * we'll need to fill the now null-able recipient object with some additional information.
 *
 * Due to this function, the existing components should feel no change for the data coming from the server.
 *
 * @param {object} originalRecipient
 * @param {string} type
 * @returns {object} - the now filled recipient object that:
 *      1. Won't crash the components (hopefully)
 *      2. contains information for displaying the correct text in the incident console, such as:
 *          - Deleted User for deleted incident commander
 *          - Deleted Resolver for deleted resolver
 *          - Deleted Stakeholder for deleted stakeholder
 */
export function getFilledRecipient(
  originalRecipient,
  type = DELETED_USER_TYPES.RESOLVER,
) {
  const isDeleted = isRecipientDeleted(originalRecipient, type);

  if (!isDeleted) return originalRecipient;

  switch (type) {
    case DELETED_USER_TYPES.INCIDENT_COMMANDER:
      return {
        ...originalRecipient,
        resolver: {
          recipientType: RECIPIENT_TYPES.PERSON, // So the PersonResolverCard component is used, in places such as Resolver or ResolverInfoCard
          id: 'deleted', // to prevent the UserInfoCard from showing "Not Available"
          isDeleted: true, // can't use id === 'deleted' because user can set that too. Check isDeleted function
          type: DELETED_USER_TYPES.INCIDENT_COMMANDER, // So Deleted User text can be shown
        },
      };
    case DELETED_USER_TYPES.RESOLVER:
      return {
        ...originalRecipient,
        engagers: [{}], // To prevent crashing
        resolver: {
          recipientType: RECIPIENT_TYPES.PERSON, // So the PersonResolverCard component is used, in places such as Resolver or ResolverInfoCard
          id: 'deleted', // to prevent the UserInfoCard from showing "Not Available"
          isDeleted: true, // can't use id === 'deleted' because user can set that too. Check isDeleted function
          type: DELETED_USER_TYPES.RESOLVER, // So Deleted Resolver text can be shown
        },
      };
    case DELETED_USER_TYPES.STAKEHOLDER:
      return {
        type, // So Deleted Stakeholder text can be shown
        isDeleted: true,
      };
    case DELETED_USER_TYPES.ENGAGED_RESOLVER:
      return {
        isDeleted: true,
        recipientType: RECIPIENT_TYPES.PERSON,
        type: DELETED_USER_TYPES.RESOLVER,
        id: 'deleted',
      };
    default:
      // Ideally we should never hit the default case. If we do, we may have a bug.
      /* eslint-disable no-console */
      console.warn(
        'Hitting the default case of getFilledRecipient. This might be a bug...',
      );
      console.warn('Check your parameters:', { originalRecipient, type });
      /* eslint-enable no-console */
      return {
        ...originalRecipient,
        resolver: {
          recipientType: RECIPIENT_TYPES.PERSON,
          id: 'deleted',
          isDeleted: true,
          type: DELETED_USER_TYPES.RESOLVER,
        },
      };
  }
}

export function getIncidentType(incident) {
  if (incident?.type && incident?.type?.name) {
    return incident.type.name;
  }
  return null;
}
